import React, { useState, useEffect } from 'react'
import {
  Box,
  Button,
  Input,
  VStack,
  HStack,
  Text,
  Heading,
  Spinner,
  Alert,
  CheckboxRoot as Checkbox,
  DialogRoot,
  DialogBackdrop,
  DialogPositioner,
  DialogContent,
  DialogHeader,
  DialogBody,
} from '@chakra-ui/react'
import { twoFactorAuthService, type DeviceInfo } from '../../services/twoFactorAuthService'
import { toaster } from '../ui/toaster-instance'

interface TwoFactorVerificationProps {
  isOpen: boolean
  onClose?: () => void
  onVerify: (success: boolean, code?: string) => void
  title?: string
  description?: string
  allowSkip?: boolean
}

// Modal using Chakra Dialog (compact on mobile)
const CustomModal: React.FC<{
  isOpen: boolean
  onClose?: () => void
  title: string
  description?: string
  children: React.ReactNode
  allowSkip?: boolean
  onSkip?: () => void
}> = ({ isOpen, onClose, title, description, children }) => {
  return (
    <DialogRoot open={isOpen} onOpenChange={(e) => (e.open ? null : onClose && onClose())} placement="center" size={{ base: 'xs', md: 'sm' }}>
      <DialogBackdrop />
      <DialogPositioner>
        <DialogContent>
          <DialogHeader>
            <HStack justify="space-between" align="center">
              <VStack align="flex-start" gap={1}>
                <Heading size="lg">{title}</Heading>
                {description && <Text fontSize="sm" color="gray.600">{description}</Text>}
              </VStack>
              {onClose && (
                <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close">
                  ×
                </Button>
              )}
            </HStack>
          </DialogHeader>
          <DialogBody p={0}>{children}</DialogBody>
        </DialogContent>
      </DialogPositioner>
    </DialogRoot>
  )
}

export const TwoFactorVerification: React.FC<TwoFactorVerificationProps> = ({
  isOpen,
  onClose,
  onVerify,
  title = 'Two-Factor Authentication',
  description = 'Enter the 6-digit code from your authenticator app',
  allowSkip = false,
}) => {
  const [code, setCode] = useState('')
  const [trustDevice, setTrustDevice] = useState(false)
  const [isVerifying, setIsVerifying] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen) {
      setCode('')
      setError(null)
      setTrustDevice(false)
    }
  }, [isOpen])

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6)
    setCode(value)
    setError(null)
  }

  const handleVerify = async () => {
    if (code.length !== 6) {
      setError('Please enter a valid 6-digit code')
      return
    }

    try {
      setIsVerifying(true)
      setError(null)

      const deviceInfo: DeviceInfo | undefined = trustDevice
        ? twoFactorAuthService.getDeviceInfo()
        : undefined

      const result = await twoFactorAuthService.verifyCode(code, trustDevice, deviceInfo)

      if (result.success) {
        toaster.create({
          title: 'Verified',
          description: result.message || 'Verification successful',
          type: 'success',
          duration: 2000,
          closable: true,
        })
        onVerify(true, code)
        setCode('')
        if (onClose) {
          onClose()
        }
      } else {
        setError(result.error || result.message || 'Invalid verification code')
        toaster.create({
          title: 'Verification Failed',
          description: result.error || result.message || 'Invalid verification code',
          type: 'error',
          duration: 5000,
          closable: true,
        })
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to verify code'
      setError(errorMessage)
      toaster.create({
        title: 'Error',
        description: errorMessage,
        type: 'error',
        duration: 5000,
        closable: true,
      })
    } finally {
      setIsVerifying(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && code.length === 6 && !isVerifying) {
      handleVerify()
    }
  }

  if (!isOpen) return null

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      description={description}
      allowSkip={allowSkip}
    >
      <Box p={6}>
        <VStack gap={6} align="stretch">
          <Alert.Root status="info">
            <Alert.Indicator />
            <Alert.Content>
              <Text fontSize="sm">
                You can use either a code from your authenticator app or a backup code.
              </Text>
            </Alert.Content>
          </Alert.Root>

          <VStack gap={2} align="stretch">
            <Text fontWeight="medium" fontSize="sm">
              Verification Code
            </Text>
            <Input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={code}
              onChange={handleCodeChange}
              onKeyPress={handleKeyPress}
              placeholder="000000"
              maxLength={6}
              size="lg"
              textAlign="center"
              fontSize="2xl"
              letterSpacing="0.2em"
              autoFocus
              disabled={isVerifying}
            />
            <Text fontSize="xs" color="gray.600" textAlign="center">
              Enter 6-digit code from authenticator app or backup code
            </Text>
          </VStack>

          <Checkbox
            checked={trustDevice}
            onChange={(e) => setTrustDevice((e.target as HTMLInputElement).checked)}
            disabled={isVerifying}
          >
            <Text fontSize="sm">Trust this device for 30 days</Text>
          </Checkbox>

          {error && (
            <Alert.Root status="error">
              <Alert.Indicator />
              <Alert.Content>
                <Text fontSize="sm">{error}</Text>
              </Alert.Content>
            </Alert.Root>
          )}

          <HStack gap={3} justify="flex-end">
            {allowSkip && onClose && (
              <Button variant="ghost" onClick={onClose} disabled={isVerifying}>
                Skip
              </Button>
            )}
            <Button
              colorScheme="green"
              onClick={handleVerify}
              disabled={code.length !== 6 || isVerifying}
              loading={isVerifying}
            >
              {isVerifying ? (
                <HStack gap={2}>
                  <Spinner size="sm" />
                  <Text>Verifying...</Text>
                </HStack>
              ) : (
                'Verify'
              )}
            </Button>
          </HStack>
        </VStack>
      </Box>
    </CustomModal>
  )
}
